import _ from 'lodash'
import type { BeckyExperiments } from '@wix/thunderbolt-commons'
import type { DataMapName } from '@wix/thunderbolt-becky-types'
import type { EditorCssFeatures, ViewerCssFeatures } from './cssFeatures.types'
import { componentCssFactory } from './componentCssCompNode'
import { componentPatternsFactory } from './commonCssData/patternsCompNode'
import {
	componentVisibilityTransformationsFactory,
	containerVisibilityTransformationsFactory,
} from './commonCssData/visibilityTransformationsCompNode'
import {
	editorResponsiveLayoutCssFeature,
	responsiveLayoutCssFeature,
} from './responsiveLayout/responsiveLayoutCssFeature'
import { variablesCssFeature } from './variables/variablesCssFeature'
import { styleBasedFeature } from './styleBased/styleBasedFeature'
import { screenInCssFeature } from './screenIn/screenInCssFeature'
import { accessibilityCssFeature } from './accessibilityStyle/accessibilityStyleCssFeature'
import { motionEffectsCssFeature } from './motionEffects/motionEffectsCssFeature'
import { landingPageCssFeature } from './landingPageCss/landingPageCssFeature'
import { transitionsCssFeature } from './transitions/transitionsCssFeature'
import { editorPinnedLayoutCssFeature, pinnedLayoutCssFeature } from './pinnedLayout/pinnedLayoutCssFeature'
import { transformationsCssFeature } from './transformations/transformationsCssFeature'
import { layoutRuntimeApiCssFeature } from './runtimeLayout/layoutRuntimeApiCssFeature'
import { compsMeasuresCssFeature, editorCompsMeasuresCssFeature } from './compsMeasures/compsMeasuresCssFeature'
import { editorZIndexCssFeature, zIndexCssFeature } from './zIndexCss/zIndexCssFeature'
import { editorFontFacesCssFeature, fontFacesCssFeature } from './fontFaces/fontFacesFeature'
import { editorStylableCssFeature, stylableCssFeature } from './stylable/stylableFeature'
import { cursorCssFeature } from './cursor/cursorCssFeature'

export const viewerCssFeatures: ViewerCssFeatures = {
	landingPages: landingPageCssFeature,
	responsiveLayout: responsiveLayoutCssFeature,
	variables: variablesCssFeature,
	styleBased: styleBasedFeature,
	screenIn: screenInCssFeature,
	motionEffects: motionEffectsCssFeature,
	transitions: transitionsCssFeature,
	transformations: transformationsCssFeature,
	accessibility: accessibilityCssFeature,
	compsMeasuresCss: compsMeasuresCssFeature,
	zIndexCss: zIndexCssFeature,
	fontFaces: fontFacesCssFeature,
	stylableCss: stylableCssFeature,
	pinnedLayout: pinnedLayoutCssFeature,
	cursor: cursorCssFeature,
}

export const editorCssFeatures: EditorCssFeatures = {
	...viewerCssFeatures,
	compsMeasuresCss: editorCompsMeasuresCssFeature,
	layoutRuntime: layoutRuntimeApiCssFeature,
	stylableCss: editorStylableCssFeature,
	pinnedLayout: editorPinnedLayoutCssFeature,
	responsiveLayout: editorResponsiveLayoutCssFeature,
	zIndexCss: editorZIndexCssFeature,
	fontFaces: editorFontFacesCssFeature,
}

export const defaultDataMaps: Array<DataMapName> = ['patterns', 'variants_data']

export type IsExperimentOpen = (experimentName: keyof BeckyExperiments) => boolean

export const componentCssFeaturesProvider = (
	componentCssFeatures: Partial<ViewerCssFeatures | EditorCssFeatures>,
	isExperimentOpen: IsExperimentOpen
) => {
	const activeCssFeatures = _.pickBy(
		componentCssFeatures,
		({ experimentName }) => experimentName === 'OPEN_TO_ALL' || isExperimentOpen(experimentName)
	)

	const componentNodes = Object.values(activeCssFeatures).reduce(
		(acc, { renderableNodes, intermediateNodes }) => ({ ...acc, ...renderableNodes, ...intermediateNodes }),
		{
			repeaterPatterns: componentPatternsFactory(),
			componentCss: componentCssFactory(activeCssFeatures),
			...(isExperimentOpen('specs.thunderbolt.catharsis_transformations_style') && {
				componentVisibilityTransformations: componentVisibilityTransformationsFactory(),
				visibilityTransformations: containerVisibilityTransformationsFactory(),
			}),
		}
	)

	const usedDataMaps = new Set(
		Object.values(activeCssFeatures)
			.flatMap(({ dataMaps }) => dataMaps)
			.concat(defaultDataMaps)
			.concat(
				isExperimentOpen('specs.thunderbolt.catharsis_transformations_style') ? ['transformations_data'] : []
			)
	)

	return { componentNodes, usedDataMaps }
}
